/* Activiteiten & Ontwikkeling — bibliotheek, planner en execute-flow.
 * Auto-verwerking naar bestaand schriftje bij afronden. */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, Heart, Calendar, Play, Copy, Package, BarChart3, Cloud, CloudRain, CalendarDays, Camera, ShieldCheck, Palette, Music, Leaf, FlaskConical, BookOpen, ChefHat, Smartphone, Hand, Users2, Moon, Award, Trophy, Puzzle, Sun, Home, TreePine, ChevronDown, ChevronUp, Plus, Clock, Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Icon + accent-kleur per categorie (voor visuele herkenning door leidsters)
const CATEGORY_META = {
  "sport en bewegen": { icon: Trophy, color: "bg-emerald-100 text-emerald-700" },
  "creatief": { icon: Palette, color: "bg-fuchsia-100 text-fuchsia-700" },
  "muziek, expressie en drama": { icon: Music, color: "bg-purple-100 text-purple-700" },
  "natuur en duurzaamheid": { icon: Leaf, color: "bg-green-100 text-green-700" },
  "techniek en proefjes": { icon: FlaskConical, color: "bg-cyan-100 text-cyan-700" },
  "taal en communicatie": { icon: BookOpen, color: "bg-blue-100 text-blue-700" },
  "cognitieve ontwikkeling": { icon: Puzzle, color: "bg-indigo-100 text-indigo-700" },
  "koken en voeding": { icon: ChefHat, color: "bg-orange-100 text-orange-700" },
  "mediawijsheid": { icon: Smartphone, color: "bg-slate-100 text-slate-700" },
  "zintuiglijke ontwikkeling": { icon: Hand, color: "bg-pink-100 text-pink-700" },
  "sociaal-emotionele ontwikkeling": { icon: Users2, color: "bg-rose-100 text-rose-700" },
  "rust en ontspanning": { icon: Moon, color: "bg-sky-100 text-sky-700" },
  "zelfstandigheid": { icon: Award, color: "bg-amber-100 text-amber-700" },
};
const DEFAULT_META = { icon: Sparkles, color: "bg-neutral-100 text-neutral-700" };
const getMeta = (cat) => CATEGORY_META[cat] || DEFAULT_META;

const CATEGORIES = ["sport en bewegen","creatief","muziek, expressie en drama","natuur en duurzaamheid","techniek en proefjes","taal en communicatie","cognitieve ontwikkeling","koken en voeding","mediawijsheid","zintuiglijke ontwikkeling","sociaal-emotionele ontwikkeling","rust en ontspanning","zelfstandigheid"];
const ONTWIKKELING = ["taal en communicatie","cognitieve ontwikkeling","grove motoriek","fijne motoriek","sociaal-emotionele ontwikkeling","creativiteit","zintuiglijke ontwikkeling","zelfredzaamheid","executieve functies","natuur- en techniekoriëntatie"];
const OPVANG = [{v:"baby",l:"Baby"},{v:"kdv",l:"KDV"},{v:"peuter",l:"Peuter"},{v:"bso",l:"BSO"}];

export default function ActiviteitenPage() {
  const [tab, setTab] = useState("bibliotheek"); // bibliotheek | planner | themas | materialen | inzichten | review
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  return (
    <div>
      <PageHeader title="Activiteiten" subtitle="Zoek, plan, voer uit en verwerk automatisch in het schriftje." testid="activiteiten-header" />
      <div className="flex flex-wrap gap-2 mb-4" data-testid="activiteiten-tabs">
        {[
          ["bibliotheek","Bibliotheek",Sparkles],
          ["planner","Planner",Calendar],
          ["suggesties","Suggesties",Sparkles],
          ["themas","Thema's",CalendarDays],
          ["materialen","Materialen",Package],
          ["inzichten","Inzichten",BarChart3],
          ...(isSuperadmin ? [["review","Review",ShieldCheck]] : []),
        ].map(([k,l,Ic]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 ${tab===k?"bg-[color:var(--sage)] text-white":"bg-neutral-100 text-neutral-700"}`}>
            <Ic className="h-4 w-4" /> {l}
          </button>
        ))}
      </div>
      {tab === "bibliotheek" && <Bibliotheek />}
      {tab === "planner" && <Planner />}
      {tab === "suggesties" && <SuggestiesTab />}
      {tab === "themas" && <Themas />}
      {tab === "materialen" && <Materialen />}
      {tab === "inzichten" && <Inzichten />}
      {tab === "review" && isSuperadmin && <ReviewTab />}
    </div>
  );
}

function Bibliotheek() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [opv, setOpv] = useState("all");
  const [cat, setCat] = useState("all");
  const [ontw, setOntw] = useState("all");
  const [favOnly, setFavOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showSchedule, setShowSchedule] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [recommended, setRecommended] = useState({ items: [], children_count: 0, opvang_breakdown: {}, message: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (q) params.q = q;
      if (opv !== "all") params.opvangsoort = opv;
      if (cat !== "all") params.categorie = cat;
      if (ontw !== "all") params.ontwikkelingsgebied = ontw;
      if (favOnly) params.favorites_only = true;
      const r = await api.get("/activities", { params });
      setItems(r.data.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [q, opv, cat, ontw, favOnly]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/activities/recommended", { params: { limit: 6 } });
        setRecommended(r.data || { items: [] });
      } catch (e) {
        setRecommended({ items: [], children_count: 0, opvang_breakdown: {}, message: "" });
      }
    })();
  }, []);

  const toggleFav = async (id) => {
    await api.post(`/activities/${id}/favorite`);
    load();
  };
  const duplicate = async (id) => {
    await api.post(`/activities/${id}/duplicate`);
    toast.success("Gekopieerd naar concept");
    load();
  };

  return (
    <div>
      {/* Hero — Voor jou vandaag */}
      <Card className="p-5 mb-4 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border-amber-200 shadow-none" data-testid="aanbevolen-vandaag">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="w-9 h-9 rounded-full bg-amber-500 text-white flex items-center justify-center">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-xl text-neutral-900 leading-tight">Voor jou vandaag</div>
            <div className="text-xs text-neutral-600">
              {recommended.based_on === "presentie" && recommended.children_count > 0 && (
                <>Op basis van {recommended.children_count} aangemelde kind{recommended.children_count === 1 ? "" : "eren"} · {Object.entries(recommended.opvang_breakdown || {}).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(", ")}</>
              )}
              {recommended.based_on === "scope" && recommended.children_count > 0 && (<>Op basis van jouw groepsprofiel</>)}
              {recommended.based_on === "algemeen" && (<>Algemene inspiratie — pas de filters aan om verder te zoeken</>)}
            </div>
          </div>
        </div>
        {recommended.items.length === 0 ? (
          <div className="text-sm text-neutral-600" data-testid="aanbevolen-empty">
            {recommended.message || "Nog geen activiteiten beschikbaar."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {recommended.items.slice(0, 6).map((a, idx) => {
              const cat = (a.categorieen || [])[0];
              const meta = getMeta(cat);
              const Icon = meta.icon;
              return (
                <Card
                  key={a.id}
                  data-testid={`aanbevolen-${a.id}`}
                  className="p-3 bg-white shadow-none border border-white hover:border-amber-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-neutral-800 text-sm leading-snug line-clamp-2">{a.titel}</div>
                      <div className="text-[11px] text-neutral-500 mt-0.5 flex items-center gap-2">
                        <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" />{a.duur_min}min</span>
                        <span>·</span>
                        <span className="capitalize">{a.binnen_buiten}</span>
                        {idx === 0 && <Badge className="bg-amber-500 text-white text-[9px] py-0 px-1.5 ml-1">Top tip</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1.5 mt-3">
                    <Button
                      size="sm"
                      onClick={() => setShowSchedule(a)}
                      style={{ background: "var(--sage)" }}
                      className="text-white flex-1"
                      data-testid={`aanbevolen-plan-${a.id}`}
                    >
                      <Calendar className="h-3.5 w-3.5 mr-1" /> Inplannen
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelected(a)} className="px-2" data-testid={`aanbevolen-detail-${a.id}`}>
                      Bekijk
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </Card>

      {/* Zoekbalk + Quick-Tabs */}
      <div className="space-y-3 mb-4">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex-1 min-w-[220px]">
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Zoek in de bibliotheek…"
              className="h-10"
              data-testid="lib-q"
            />
          </div>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer select-none px-2">
            <Checkbox checked={favOnly} onCheckedChange={v => setFavOnly(!!v)} data-testid="lib-fav-only" />
            <Heart className={`h-4 w-4 ${favOnly ? "text-red-500 fill-current" : "text-neutral-400"}`} /> Favorieten
          </label>
          <Button
            variant="ghost"
            onClick={() => setShowFilters(!showFilters)}
            className="text-neutral-600"
            data-testid="lib-toggle-filters"
          >
            Meer filters {showFilters ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
          </Button>
          <Button onClick={() => setShowNew(true)} variant="outline" data-testid="lib-new-btn" className="border-[color:var(--sage)] text-[color:var(--sage)]">
            <Plus className="h-4 w-4 mr-1" /> Eigen activiteit
          </Button>
        </div>

        {/* Quick-Tabs op leeftijdscategorie */}
        <div className="flex flex-wrap gap-1.5" data-testid="opvang-quick-tabs">
          {[["all","Alle leeftijden"], ["baby","👶 Baby"], ["kdv","🧒 KDV"], ["peuter","🌱 Peuter"], ["bso","🎒 BSO"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setOpv(k)}
              data-testid={`opvang-tab-${k}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition ${
                opv === k
                  ? "bg-[color:var(--sage)] text-white shadow-sm"
                  : "bg-white border border-neutral-200 text-neutral-700 hover:border-[color:var(--sage)]"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {showFilters && (
          <div className="flex flex-wrap gap-3 items-end pt-2 border-t border-neutral-100" data-testid="bibliotheek-filters">
            <div>
              <Label className="text-xs">Categorie</Label>
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value="all">Alle categorieën</SelectItem>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ontwikkelingsgebied</Label>
              <Select value={ontw} onValueChange={setOntw}>
                <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value="all">Alle gebieden</SelectItem>
                  {ONTWIKKELING.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(cat !== "all" || ontw !== "all") && (
              <Button variant="ghost" size="sm" onClick={() => { setCat("all"); setOntw("all"); }} data-testid="lib-clear-filters">
                Filters wissen
              </Button>
            )}
          </div>
        )}
      </div>

      {loading && items.length === 0 && <div className="text-center py-10 text-sm text-neutral-400" data-testid="lib-loading">Activiteiten laden…</div>}
      {!loading && items.length === 0 && <div className="text-center py-10 text-sm text-neutral-500" data-testid="lib-empty">Geen activiteiten gevonden.</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(a => {
          const cat = (a.categorieen || [])[0];
          const meta = getMeta(cat);
          const Icon = meta.icon;
          return (
            <Card key={a.id} className="p-4 bg-white shadow-none space-y-3 hover:shadow-md hover:border-[color:var(--sage)] transition-all" data-testid={`activity-card-${a.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setSelected(a)}>
                  <div className="font-medium text-neutral-900 text-[15px] leading-snug line-clamp-2">{a.titel}</div>
                  <div className="text-[11px] text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" />{a.duur_min}min</span>
                    <span>·</span>
                    <span>{(a.opvangsoort||[]).join("/")}</span>
                    <span>·</span>
                    <span className="capitalize">{a.binnen_buiten}</span>
                  </div>
                </div>
                <button onClick={() => toggleFav(a.id)} data-testid={`fav-${a.id}`}
                  className={`shrink-0 ${a.is_favorite ? "text-red-500" : "text-neutral-300 hover:text-red-500"}`}>
                  <Heart className={`h-5 w-5 ${a.is_favorite ? "fill-current" : ""}`} />
                </button>
              </div>
              <div className="text-xs text-neutral-600 line-clamp-2">{a.korte_omschrijving}</div>
              <div className="flex items-center gap-3 text-[11px] text-neutral-500">
                <span className="inline-flex items-center gap-1" title="Aantal materialen">
                  <Package className="h-3 w-3" />
                  {(a.materialen || []).length > 0 ? `${a.materialen.length} materialen` : "Nog geen lijst"}
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {[...new Set(a.ontwikkelingsgebieden || [])].slice(0,3).map(g =>
                  <Badge key={g} variant="outline" className="text-[10px] bg-[color:var(--sage-100)] text-[color:var(--sage)] border-transparent">{g}</Badge>
                )}
              </div>
              <div className="flex gap-1.5 pt-1">
                <Button
                  size="sm"
                  onClick={() => setShowSchedule(a)}
                  style={{ background: "var(--sage)" }}
                  className="text-white flex-1"
                  data-testid={`schedule-${a.id}`}
                >
                  <Calendar className="h-3.5 w-3.5 mr-1" /> Inplannen
                </Button>
                <Button size="sm" variant="ghost" onClick={() => duplicate(a.id)} title="Dupliceer als concept" data-testid={`dup-${a.id}`} className="px-2">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {selected && <DetailDialog activity={selected} onClose={() => setSelected(null)} onSchedule={() => { setShowSchedule(selected); setSelected(null); }} />}
      {showNew && <ActivityForm onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {showSchedule && <ScheduleDialog activity={showSchedule} onClose={() => setShowSchedule(null)} />}
    </div>
  );
}

function DetailDialog({ activity: initialActivity, onClose, onSchedule }) {
  const [activity, setActivity] = useState(initialActivity);
  const [genMats, setGenMats] = useState(false);
  const [markingIdx, setMarkingIdx] = useState(null);
  const [marked, setMarked] = useState(new Set());
  const hasMats = (activity.materialen || []).length > 0;

  const genAIMaterials = async () => {
    setGenMats(true);
    try {
      const r = await api.post(`/activities/${activity.id}/ai-materialen`);
      setActivity({ ...activity, materialen: r.data.materialen || [], materialen_ai_generated: r.data.ai_generated });
      toast.success(r.data.cached ? "Bestaande lijst geladen" : "AI-lijst gegenereerd ✨");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenMats(false);
    }
  };

  const markMissing = async (mat, idx) => {
    setMarkingIdx(idx);
    try {
      await api.post("/materials/inventory", { naam: mat.naam, aanwezig: false });
      setMarked(prev => new Set([...prev, idx]));
      toast.success(`"${mat.naam}" op de bestellijst gezet`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMarkingIdx(null);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="activity-detail">
        <DialogHeader>
          <DialogTitle>{activity.titel}</DialogTitle>
          <DialogDescription>{activity.duur_min}min · {(activity.opvangsoort||[]).join(", ")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div><strong>Voor ouders:</strong> {activity.ouder_tekst}</div>
          <div><strong>Intern:</strong> {activity.korte_omschrijving}</div>

          {/* Materialenlijst — prominent zichtbaar met AI-fallback */}
          <Card className="p-3 bg-neutral-50 border-neutral-200 shadow-none" data-testid="detail-materials">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Package className="h-4 w-4 text-neutral-700" />
                <strong className="text-neutral-800">Wat je nodig hebt</strong>
                {activity.materialen_ai_generated && <Badge variant="outline" className="text-[9px] py-0"><Sparkles className="h-2.5 w-2.5 mr-0.5" />AI</Badge>}
              </div>
              {!hasMats && (
                <Button size="sm" onClick={genAIMaterials} disabled={genMats}
                  className="text-white bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600"
                  data-testid="detail-ai-materials-btn">
                  <Sparkles className="h-3.5 w-3.5 mr-1" /> {genMats ? "Bezig…" : "AI-lijst maken"}
                </Button>
              )}
            </div>
            {hasMats ? (
              <ul className="space-y-1" data-testid="detail-materials-list">
                {activity.materialen.map((m, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm py-1 border-b border-neutral-100 last:border-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--sage)]"></span>
                    <span className="flex-1">{m.naam}</span>
                    {(m.hoeveelheid || m.eenheid) && (
                      <span className="text-xs text-neutral-500">{m.hoeveelheid ? `${m.hoeveelheid}${m.eenheid ? ` ${m.eenheid}` : ""}` : m.eenheid}</span>
                    )}
                    {marked.has(i) ? (
                      <Badge className="bg-amber-100 text-amber-800 border-transparent text-[10px]" data-testid={`mat-marked-${i}`}>
                        ✓ op bestellijst
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => markMissing(m, i)}
                        disabled={markingIdx === i}
                        className="h-6 px-2 text-[10px] text-amber-700 hover:bg-amber-50"
                        data-testid={`mat-mark-missing-${i}`}
                        title="Toevoegen aan bestellijst"
                      >
                        {markingIdx === i ? "…" : "Ontbreekt"}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-neutral-500 italic">Nog geen materialenlijst — klik op <strong>AI-lijst maken</strong> voor een concreet overzicht.</div>
            )}
          </Card>

          {activity.stappen?.length > 0 && (
            <div><strong>Uitvoering:</strong>
              <ol className="list-decimal pl-5">{activity.stappen.map((s,i) => <li key={i}>{s}</li>)}</ol>
            </div>
          )}
          {activity.veiligheid && <div className="p-2 bg-amber-50 text-amber-800 text-xs rounded">⚠ {activity.veiligheid}</div>}
          <div className="flex flex-wrap gap-1">
            {(activity.ontwikkelingsgebieden||[]).map(g => <Badge key={g} className="bg-[color:var(--sage-100)] text-[color:var(--sage)]">{g}</Badge>)}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Sluiten</Button>
          <Button onClick={onSchedule} style={{background:"var(--sage)"}} className="text-white" data-testid="detail-schedule">
            <Calendar className="h-4 w-4 mr-1" /> Inplannen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActivityForm({ onClose, onSaved }) {
  const [form, setForm] = useState({ titel: "", korte_omschrijving: "", ouder_tekst: "", opvangsoort: [], categorieen: [], ontwikkelingsgebieden: [], duur_min: 30, binnen_buiten: "binnen", stappen: [], materialen: [] });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.titel.trim()) return toast.error("Titel is verplicht");
    setBusy(true);
    try {
      await api.post("/activities", form);
      toast.success("Activiteit aangemaakt");
      onSaved();
    } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white" data-testid="new-activity-dialog">
        <DialogHeader><DialogTitle>Nieuwe activiteit</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <div><Label>Titel</Label><Input value={form.titel} onChange={e=>setForm({...form,titel:e.target.value})} data-testid="new-titel" /></div>
          <div><Label>Korte omschrijving</Label><Textarea rows={2} value={form.korte_omschrijving} onChange={e=>setForm({...form,korte_omschrijving:e.target.value})} /></div>
          <div><Label>Oudertekst</Label><Textarea rows={2} value={form.ouder_tekst} onChange={e=>setForm({...form,ouder_tekst:e.target.value})} placeholder="Begrijpelijk voor ouders" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Duur (min)</Label><Input type="number" value={form.duur_min} onChange={e=>setForm({...form,duur_min:parseInt(e.target.value||"0")})} /></div>
            <div><Label>Binnen/Buiten</Label>
              <Select value={form.binnen_buiten} onValueChange={v=>setForm({...form,binnen_buiten:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="binnen">Binnen</SelectItem>
                  <SelectItem value="buiten">Buiten</SelectItem>
                  <SelectItem value="beide">Beide</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuleren</Button>
          <Button onClick={submit} disabled={busy} style={{background:"var(--sage)"}} className="text-white" data-testid="new-save">Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleDialog({ activity, onClose }) {
  const [groups, setGroups] = useState([]);
  const [group_id, setGroupId] = useState("");
  const [datum, setDatum] = useState(new Date().toISOString().slice(0,10));
  const [start_tijd, setStart] = useState("09:30");
  const [eind_tijd, setEind] = useState("10:15");
  const [busy, setBusy] = useState(false);
  const [repeat, setRepeat] = useState("once"); // once | daily | weekly | custom
  const [endDate, setEndDate] = useState(datum);
  const [weekdays, setWeekdays] = useState([]);
  useEffect(() => { api.get("/groups").then(r => setGroups(r.data || [])); }, []);
  useEffect(() => { setEndDate(datum); }, [datum]);
  const toggleWd = (n) => setWeekdays(w => w.includes(n) ? w.filter(x => x !== n) : [...w, n]);
  const submit = async () => {
    if (!group_id) return toast.error("Kies een groep");
    const grp = groups.find(g => g.id === group_id);
    setBusy(true);
    try {
      if (repeat === "once") {
        await api.post("/scheduled-activities", { activity_id: activity.id, group_id, location_id: grp.location_id, datum, start_tijd, eind_tijd });
        toast.success(`'${activity.titel}' ingepland op ${datum}`);
      } else {
        let wd = [];
        if (repeat === "daily") wd = [0,1,2,3,4,5,6];
        else if (repeat === "weekly") wd = [new Date(datum).getDay() === 0 ? 6 : new Date(datum).getDay()-1];
        else wd = weekdays;
        if (wd.length === 0) { toast.error("Kies minimaal één weekdag"); setBusy(false); return; }
        const r = await api.post("/scheduled-activities/bulk", {
          activity_id: activity.id, group_id, location_id: grp.location_id,
          start_date: datum, end_date: endDate, weekdays: wd,
          start_tijd, eind_tijd, skip_existing: true,
        });
        toast.success(`${r.data.count} keer ingepland` + (r.data.skipped?.length ? ` (${r.data.skipped.length} overgeslagen)` : ""));
      }
      onClose();
    } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white" data-testid="schedule-dialog">
        <DialogHeader><DialogTitle>Inplannen: {activity.titel}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <div><Label>Groep</Label>
            <Select value={group_id} onValueChange={setGroupId}>
              <SelectTrigger data-testid="sched-group"><SelectValue placeholder="Kies groep" /></SelectTrigger>
              <SelectContent className="bg-white">{groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>{repeat === "once" ? "Datum" : "Startdatum"}</Label><Input type="date" value={datum} onChange={e=>setDatum(e.target.value)} data-testid="sched-datum" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Start</Label><Input type="time" value={start_tijd} onChange={e=>setStart(e.target.value)} /></div>
            <div><Label>Eind</Label><Input type="time" value={eind_tijd} onChange={e=>setEind(e.target.value)} /></div>
          </div>
          <div className="border-t border-neutral-100 pt-2">
            <Label>Herhalen</Label>
            <Select value={repeat} onValueChange={setRepeat}>
              <SelectTrigger data-testid="sched-repeat"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="once">Eenmalig</SelectItem>
                <SelectItem value="daily">Dagelijks</SelectItem>
                <SelectItem value="weekly">Wekelijks (zelfde dag)</SelectItem>
                <SelectItem value="custom">Kies weekdagen</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {repeat !== "once" && (
            <div>
              <Label>Einddatum</Label>
              <Input type="date" value={endDate} min={datum} onChange={e=>setEndDate(e.target.value)} data-testid="sched-end" />
            </div>
          )}
          {repeat === "custom" && (
            <div className="flex flex-wrap gap-1" data-testid="sched-weekdays">
              {["Ma","Di","Wo","Do","Vr","Za","Zo"].map((n,i) => (
                <button key={i} type="button"
                  onClick={() => toggleWd(i)}
                  data-testid={`wd-${i}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium border ${weekdays.includes(i) ? "bg-[color:var(--sage)] text-white border-[color:var(--sage)]" : "bg-white text-neutral-700 border-neutral-300"}`}
                >{n}</button>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuleren</Button>
          <Button onClick={submit} disabled={busy} style={{background:"var(--sage)"}} className="text-white" data-testid="sched-save">
            {busy ? "Inplannen…" : (repeat === "once" ? "Inplannen" : "Serie plannen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Planner() {
  const [items, setItems] = useState([]);
  const today = new Date().toISOString().slice(0,10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [execFor, setExecFor] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const load = async () => {
    const r = await api.get("/scheduled-activities", { params: { date_from: from, date_to: to } });
    setItems(r.data || []);
  };
  useEffect(() => { load(); }, [from, to]);
  const cancel = async (id) => {
    if (!window.confirm("Annuleren?")) return;
    await api.delete(`/scheduled-activities/${id}`);
    toast.success("Geannuleerd");
    load();
  };
  return (
    <div>
      <div className="flex gap-2 items-end mb-4">
        <div><Label className="text-xs">Van</Label><Input type="date" value={from} onChange={e=>setFrom(e.target.value)} data-testid="planner-from" /></div>
        <div><Label className="text-xs">T/m</Label><Input type="date" value={to} onChange={e=>setTo(e.target.value)} data-testid="planner-to" /></div>
        <Button onClick={() => setShowAI(true)} data-testid="planner-ai-btn"
          style={{background:"linear-gradient(135deg,#a68bd8,#8065c0)"}} className="text-white">
          <Sparkles className="h-4 w-4 mr-1" /> AI-weekplan
        </Button>
      </div>
      {items.length === 0 && <div className="text-center py-10 text-sm text-neutral-500" data-testid="planner-empty">Geen geplande activiteiten.</div>}
      <div className="space-y-2">
        {items.map(s => (
          <Card key={s.id} className={`p-3 bg-white shadow-none flex flex-wrap items-center gap-3 ${s.slechtweer_active ? "border-2 border-blue-300 bg-blue-50/30" : ""}`} data-testid={`sched-${s.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-neutral-800 truncate flex items-center gap-1.5">
                {s.slechtweer_active && <CloudRain className="h-4 w-4 text-blue-600 shrink-0" />}
                {s.activity_titel || "?"}
                {s.slechtweer_active && <span className="text-[10px] text-blue-700 font-normal">(slechtweer-modus)</span>}
              </div>
              <div className="text-xs text-neutral-500">{s.datum} · {s.start_tijd}-{s.eind_tijd} · {s.group_name}</div>
            </div>
            <Badge className={
              s.status === "executed" ? "bg-emerald-100 text-emerald-700" :
              s.status === "cancelled" ? "bg-red-100 text-red-700" :
              "bg-amber-100 text-amber-700"
            }>{s.status}</Badge>
            {s.status === "planned" && (
              <>
                <Button size="sm" variant="outline"
                  onClick={async () => {
                    const r = await api.post(`/scheduled-activities/${s.id}/toggle-slechtweer`);
                    toast.success(r.data.slechtweer_active ? "Slechtweer-modus aan — wij doen de binnen-versie" : "Slechtweer-modus uit");
                    load();
                  }}
                  data-testid={`slechtweer-${s.id}`}
                  className={s.slechtweer_active ? "bg-blue-500 text-white border-blue-500 hover:bg-blue-600" : "border-blue-300 text-blue-700 hover:bg-blue-50"}
                  title="Schakel naar het slechtweer-alternatief"
                >
                  {s.slechtweer_active ? <Cloud className="h-4 w-4 mr-1" /> : <CloudRain className="h-4 w-4 mr-1" />}
                  {s.slechtweer_active ? "Weer OK" : "Slecht weer"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setExecFor(s)} data-testid={`exec-${s.id}`}>
                  <Play className="h-3 w-3 mr-1" /> Afronden
                </Button>
                <Button size="sm" variant="ghost" onClick={() => cancel(s.id)} data-testid={`cancel-${s.id}`}>Annuleer</Button>
              </>
            )}
          </Card>
        ))}
      </div>
      {execFor && <ExecuteDialog scheduled={execFor} onClose={() => setExecFor(null)} onDone={() => { setExecFor(null); load(); }} />}
      {showAI && <AIWeekplanDialog onClose={() => setShowAI(false)} onDone={() => { setShowAI(false); load(); }} />}
    </div>
  );
}

function AIWeekplanDialog({ onClose, onDone }) {
  const [groups, setGroups] = useState([]);
  const [group_id, setGroupId] = useState("");
  const [thema, setThema] = useState("");
  const [opvangsoort, setOpv] = useState("");
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d.toISOString().slice(0,10);
  });
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  useEffect(() => { api.get("/groups").then(r => setGroups(r.data || [])); }, []);
  const suggest = async () => {
    if (!group_id) return toast.error("Kies groep");
    setBusy(true); setSuggestions(null);
    try {
      const r = await api.post("/ai/weekplan-suggest", { group_id, thema, week_start: weekStart, opvangsoort: opvangsoort || undefined });
      setSuggestions(r.data.suggestions || []);
      if (!r.data.suggestions?.length) toast.error("Geen voorstellen ontvangen");
    } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const accept = async () => {
    const grp = groups.find(g => g.id === group_id);
    setBusy(true);
    try {
      let n = 0;
      for (const s of suggestions) {
        await api.post("/scheduled-activities", {
          activity_id: s.activity_id, group_id, location_id: grp.location_id,
          datum: s.datum, start_tijd: "09:30", eind_tijd: "10:15",
        });
        n++;
      }
      toast.success(`${n} activiteiten ingepland`);
      onDone();
    } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto" data-testid="ai-weekplan-dialog">
        <DialogHeader>
          <DialogTitle>AI-weekplanner</DialogTitle>
          <DialogDescription>Laat de AI-assistent een weekplanning voorstellen op basis van thema, leeftijd en beschikbare activiteiten.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <div><Label>Groep</Label>
            <Select value={group_id} onValueChange={setGroupId}>
              <SelectTrigger data-testid="ai-group"><SelectValue placeholder="Kies groep" /></SelectTrigger>
              <SelectContent className="bg-white">{groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Thema</Label><Input value={thema} onChange={e=>setThema(e.target.value)} placeholder="Bijv. Herfst en natuur" data-testid="ai-thema" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Opvangsoort</Label>
              <Select value={opvangsoort || "any"} onValueChange={v => setOpv(v === "any" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="any">Alle</SelectItem>
                  <SelectItem value="baby">Baby</SelectItem>
                  <SelectItem value="kdv">KDV</SelectItem>
                  <SelectItem value="peuter">Peuter</SelectItem>
                  <SelectItem value="bso">BSO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Week (maandag)</Label><Input type="date" value={weekStart} onChange={e=>setWeekStart(e.target.value)} data-testid="ai-week" /></div>
          </div>
          <Button onClick={suggest} disabled={busy} data-testid="ai-suggest-btn" className="w-full"
            style={{background:"linear-gradient(135deg,#a68bd8,#8065c0)"}}>
            <Sparkles className="h-4 w-4 mr-1" /> {busy ? "De AI denkt na…" : "Vraag AI om voorstellen"}
          </Button>
          {suggestions && suggestions.length > 0 && (
            <div className="border-t border-neutral-100 pt-3 space-y-1.5">
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Voorstellen</div>
              {suggestions.map((s, i) => (
                <div key={i} className="bg-neutral-50 rounded p-2" data-testid={`ai-suggestion-${i}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--sage)] w-8">{s.weekdag}</span>
                    <span className="font-medium text-neutral-800 flex-1">{s.activity_titel}</span>
                    <span className="text-[10px] text-neutral-500">{s.datum}</span>
                  </div>
                  {s.motivatie && <div className="text-[11px] text-neutral-600 italic pl-10">{s.motivatie}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Sluiten</Button>
          {suggestions && suggestions.length > 0 && (
            <Button onClick={accept} disabled={busy} style={{background:"var(--sage)"}} className="text-white" data-testid="ai-accept">
              {busy ? "Inplannen…" : `Plan ${suggestions.length} activiteiten in`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExecuteDialog({ scheduled, onClose, onDone }) {
  const [aanwezig, setAanwezig] = useState([]);
  const [participants, setParticipants] = useState({}); // {kind_id: status}
  const [observatie, setObservatie] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [afterUploadPhoto, setAfterUploadPhoto] = useState(null); // {photo_id, participants_names}
  useEffect(() => {
    api.get("/planning/day", { params: { datum: scheduled.datum, group_id: scheduled.group_id } })
      .then(r => {
        const present = (r.data.items || []).filter(it => it.presentie?.aankomst && it.presentie?.aanwezig !== false);
        setAanwezig(present);
        const map = {};
        present.forEach(it => { map[it.kind.id] = "participated"; });
        setParticipants(map);
      })
      .catch(() => setAanwezig([]));
  }, [scheduled.id]);
  const submit = async () => {
    setBusy(true);
    try {
      const parts = Object.entries(participants).map(([kind_id, status]) => ({ kind_id, status }));
      const r = await api.post(`/scheduled-activities/${scheduled.id}/execute`, {
        status: "executed", participants: parts, observatie, auto_publish: true,
      });
      toast.success(`Verwerkt naar ${r.data.count} schriftje(s)`);
      // Foto uploaden na succesvolle execute — alleen voor kinderen die daadwerkelijk hebben deelgenomen.
      if (photoFile) {
        const eligible = parts.filter(p => p.status !== "absent").map(p => p.kind_id);
        if (eligible.length) {
          const fd = new FormData();
          fd.append("file", photoFile);
          fd.append("kind_ids", eligible.join(","));
          fd.append("caption", `Groepsfoto — ${scheduled.activity_titel}`);
          try {
            const up = await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Groepsfoto geüpload");
            // Open per-kind caption dialog
            const names = aanwezig.filter(it => eligible.includes(it.kind.id)).map(it => ({ id: it.kind.id, naam: `${it.kind.voornaam} ${it.kind.achternaam}` }));
            setAfterUploadPhoto({ photo: up.data, kinderen: names });
            return; // don't close yet — let user add captions
          } catch (e) {
            toast.error("Schriftjes gemaakt, maar foto uploaden mislukte: " + formatApiError(e.response?.data?.detail));
          }
        }
      }
      onDone();
    } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
    <Dialog open={!afterUploadPhoto} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white max-h-[85vh] overflow-y-auto" data-testid="execute-dialog">
        <DialogHeader>
          <DialogTitle>Afronden: {scheduled.activity_titel}</DialogTitle>
          <DialogDescription>Bevestig deelname per aanwezig kind — publicatie naar schriftje.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {aanwezig.length === 0 && <div className="text-sm text-neutral-500">Geen aanwezige kinderen — kan niet publiceren.</div>}
          {aanwezig.map(it => (
            <div key={it.kind.id} className="flex items-center gap-2 p-2 bg-neutral-50 rounded" data-testid={`part-${it.kind.id}`}>
              <div className="flex-1 text-sm">{it.kind.voornaam} {it.kind.achternaam}</div>
              <Select value={participants[it.kind.id] || "participated"} onValueChange={v => setParticipants(p => ({...p, [it.kind.id]: v}))}>
                <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="participated">Deelgenomen</SelectItem>
                  <SelectItem value="partial">Gedeeltelijk</SelectItem>
                  <SelectItem value="offered_only">Alleen aangeboden</SelectItem>
                  <SelectItem value="absent">Niet deelgenomen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
          <div>
            <Label className="text-xs">Groepsobservatie (optioneel)</Label>
            <Textarea rows={2} value={observatie} onChange={e=>setObservatie(e.target.value)} data-testid="exec-observatie"
              placeholder="Bijv. Alle kinderen deden enthousiast mee." />
          </div>
          <div className="border-t border-neutral-100 pt-3">
            <Label className="text-xs flex items-center gap-1.5">
              <Camera className="h-3.5 w-3.5" /> Groepsfoto (optioneel — verschijnt automatisch bij ieder aanwezig kind met foto-toestemming)
            </Label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={e => setPhotoFile(e.target.files?.[0] || null)}
              data-testid="exec-photo-input"
              className="w-full text-xs mt-1 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[color:var(--sage-100)] file:text-[color:var(--sage)]"
            />
            {photoFile && <div className="text-[11px] text-neutral-500 mt-1">Gekozen: {photoFile.name}</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuleren</Button>
          <Button onClick={submit} disabled={busy || aanwezig.length===0} style={{background:"var(--sage)"}} className="text-white" data-testid="exec-submit">
            {busy ? "Verwerken…" : "Publiceer naar schriftje"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {afterUploadPhoto && <PerKindCaptionDialog photo={afterUploadPhoto.photo} kinderen={afterUploadPhoto.kinderen} onDone={() => { setAfterUploadPhoto(null); onDone(); }} />}
    </>
  );
}

function PerKindCaptionDialog({ photo, kinderen, onDone }) {
  const [captions, setCaptions] = useState({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await Promise.all(
        Object.entries(captions)
          .filter(([_, v]) => (v || "").trim().length > 0)
          .map(([kind_id, caption]) =>
            api.post(`/photos/${photo.id}/kind-caption`, { kind_id, caption })
          )
      );
      const n = Object.values(captions).filter(v => (v || "").trim().length > 0).length;
      if (n > 0) toast.success(`${n} persoonlijk bijschrift(en) opgeslagen`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onDone}>
      <DialogContent className="max-w-lg bg-white max-h-[85vh] overflow-y-auto" data-testid="per-kind-caption-dialog">
        <DialogHeader>
          <DialogTitle>Persoonlijk bijschrift per kind</DialogTitle>
          <DialogDescription>Voeg optioneel een persoonlijke tekst toe die alleen die specifieke ouder ziet. Laat leeg om de groepstekst te gebruiken.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {kinderen.map(k => (
            <div key={k.id} className="border border-neutral-100 rounded p-2" data-testid={`caption-row-${k.id}`}>
              <div className="text-sm font-medium text-neutral-800 mb-1">{k.naam}</div>
              <Textarea
                rows={2}
                value={captions[k.id] || ""}
                onChange={e => setCaptions(c => ({ ...c, [k.id]: e.target.value }))}
                placeholder="Bijv. Jouw kind lachte de hele tijd tijdens deze activiteit."
                data-testid={`caption-input-${k.id}`}
                className="text-xs"
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onDone} data-testid="caption-skip">Overslaan</Button>
          <Button onClick={save} disabled={busy} style={{background:"var(--sage)"}} className="text-white" data-testid="caption-save">
            {busy ? "Opslaan…" : "Bijschriften opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Materialen() {
  const today = new Date().toISOString().slice(0,10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [view, setView] = useState("gebruik"); // gebruik | bestellijst
  const [best, setBest] = useState(null);
  const loadUsage = () => {
    api.get("/activities/materials", { params: { date_from: from, date_to: to } })
      .then(r => setData(r.data)).catch(() => setData({ items: [] }));
  };
  const loadBest = () => {
    api.get("/materials/bestellijst", { params: { date_from: from, date_to: to } })
      .then(r => setBest(r.data)).catch(() => setBest({ te_kopen: [], aanwezig: [] }));
  };
  useEffect(() => {
    if (view === "gebruik") loadUsage();
    else loadBest();
  }, [from, to, view]);
  const setAanwezig = async (m, aanwezig) => {
    try {
      await api.post("/materials/inventory", { naam: m.naam, aanwezig, location_id: m.location_id });
      toast.success(aanwezig ? "Op voorraad gemarkeerd" : "Op bestellijst gezet");
      loadBest();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <div><Label className="text-xs">Van</Label><Input type="date" value={from} onChange={e=>setFrom(e.target.value)} /></div>
        <div><Label className="text-xs">T/m</Label><Input type="date" value={to} onChange={e=>setTo(e.target.value)} /></div>
        <div className="flex gap-1">
          <button onClick={() => setView("gebruik")} data-testid="mat-view-gebruik"
            className={`px-3 py-1.5 rounded text-xs ${view === "gebruik" ? "bg-[color:var(--sage)] text-white" : "bg-neutral-100"}`}>Gebruik</button>
          <button onClick={() => setView("bestellijst")} data-testid="mat-view-bestel"
            className={`px-3 py-1.5 rounded text-xs ${view === "bestellijst" ? "bg-[color:var(--sage)] text-white" : "bg-neutral-100"}`}>Bestellijst</button>
        </div>
        <Button variant="outline" onClick={() => window.print()}>Print</Button>
      </div>
      {view === "gebruik" && (
        <>
          {(!data || data.items.length === 0) && <div className="text-center py-10 text-sm text-neutral-500" data-testid="mat-empty">Geen materialen voor deze periode.</div>}
          {data && data.items.length > 0 && (
            <table className="w-full text-sm bg-white rounded shadow-none" data-testid="mat-table">
              <thead className="text-left text-xs text-neutral-500 border-b">
                <tr><th className="p-2">Materiaal</th><th className="p-2">Aantal</th><th className="p-2">Eenheid</th><th className="p-2">Gebruikt bij</th></tr>
              </thead>
              <tbody>
                {data.items.map((m, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-2 font-medium">{m.naam}</td>
                    <td className="p-2">{m.hoeveelheid}</td>
                    <td className="p-2">{m.eenheid}</td>
                    <td className="p-2 text-xs text-neutral-500">{m.usages.map(u => `${u.datum} · ${u.titel}`).join("; ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      {view === "bestellijst" && best && (
        <div className="space-y-4">
          <Card className="p-3 bg-amber-50 border border-amber-200" data-testid="bestellijst-card">
            <div className="text-sm font-medium text-amber-800 mb-2">Bestellijst ({best.te_kopen.length})</div>
            {best.te_kopen.length === 0 && <div className="text-xs text-amber-600">Niets nodig — alles op voorraad.</div>}
            {best.te_kopen.map((m, i) => (
              <label key={i} className="flex items-center gap-2 py-1.5 text-sm cursor-pointer" data-testid={`bestel-${i}`}>
                <input type="checkbox" onChange={() => setAanwezig(m, true)} className="rounded" />
                <span className="flex-1">{m.naam}</span>
                <span className="text-xs text-neutral-500">{m.hoeveelheid} {m.eenheid}</span>
              </label>
            ))}
          </Card>
          <Card className="p-3 bg-emerald-50 border border-emerald-200" data-testid="voorraad-card">
            <div className="text-sm font-medium text-emerald-800 mb-2">Op voorraad ({best.aanwezig.length})</div>
            {best.aanwezig.map((m, i) => (
              <div key={i} className="flex items-center gap-2 py-1 text-sm" data-testid={`voorraad-${i}`}>
                <span className="text-emerald-700">✓</span>
                <span className="flex-1">{m.naam}</span>
                <span className="text-xs text-neutral-500">{m.hoeveelheid} {m.eenheid}</span>
                <button onClick={() => setAanwezig(m, false)} className="text-xs text-neutral-400 hover:text-red-600" data-testid={`voorraad-remove-${i}`}>Terug op bestellijst</button>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}

function Inzichten() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/activities/inzichten").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-center py-10 text-neutral-500">Laden…</div>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Kpi label="Gepland" v={data.planned} testid="kpi-planned" />
      <Kpi label="Uitgevoerd" v={data.executed} testid="kpi-executed" />
      <Kpi label="Geannuleerd" v={data.cancelled} testid="kpi-cancelled" />
      <Kpi label="Schriftjes gepubliceerd" v={data.diary_entries_published} testid="kpi-diary" />
      <Card className="col-span-full p-4 bg-white shadow-none">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Meest gebruikt</div>
        {(data.top_activities || []).length === 0 && <div className="text-sm text-neutral-400">Nog geen data.</div>}
        <div className="space-y-1">
          {(data.top_activities || []).map(t => (
            <div key={t.activity_id} className="flex items-center justify-between text-sm">
              <span>{t.titel}</span><span className="font-semibold">{t.count}×</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Themas() {
  const today = new Date().toISOString().slice(0, 10);
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [spreadFor, setSpreadFor] = useState(null);
  const load = async () => {
    const r = await api.get("/themes", { params: { date_from: today } });
    setItems(r.data || []);
  };
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Thema verwijderen?")) return;
    await api.delete(`/themes/${id}`);
    toast.success("Verwijderd");
    load();
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-neutral-600">Thema's en leerplannen — koppel meerdere activiteiten aan een periode.</div>
        <Button onClick={() => setShowNew(true)} style={{background:"var(--sage)"}} className="text-white" data-testid="thema-new-btn">
          + Nieuw thema
        </Button>
      </div>
      {items.length === 0 && <div className="text-center py-10 text-sm text-neutral-500" data-testid="themas-empty">Nog geen thema's gepland.</div>}
      <div className="space-y-3">
        {items.map(t => (
          <Card key={t.id} className="p-4 bg-white shadow-none" data-testid={`thema-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-neutral-800">{t.titel}</div>
                <div className="text-[11px] text-neutral-500 flex items-center gap-1.5"><CalendarDays className="h-3 w-3" /> {t.start_date} → {t.end_date} · {(t.activity_ids || []).length} activiteiten</div>
                {t.omschrijving && <div className="text-sm text-neutral-600 mt-1">{t.omschrijving}</div>}
                {(t.activity_titels || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {t.activity_titels.map((tt, i) => <Badge key={i} variant="outline" className="text-[10px]">{tt}</Badge>)}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                {(t.activity_ids || []).length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => setSpreadFor(t)} data-testid={`thema-spread-${t.id}`}
                    className="border-[color:var(--sage)] text-[color:var(--sage)] hover:bg-[color:var(--sage-100)]">
                    <Calendar className="h-3 w-3 mr-1" /> Spreid over planner
                  </Button>
                )}
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(t)} data-testid={`thema-edit-${t.id}`}>Bewerk</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(t.id)} data-testid={`thema-delete-${t.id}`} className="text-red-600">Verwijder</Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {(showNew || editing) && <ThemaForm existing={editing} onClose={() => { setShowNew(false); setEditing(null); }} onSaved={() => { setShowNew(false); setEditing(null); load(); }} />}
      {spreadFor && <ThemaSpreadDialog theme={spreadFor} onClose={() => setSpreadFor(null)} />}
    </div>
  );
}

function ThemaSpreadDialog({ theme, onClose }) {
  const [groups, setGroups] = useState([]);
  const [group_id, setGroupId] = useState("");
  const [weekdays, setWeekdays] = useState([0,1,2,3,4]); // ma-vr
  const [start_tijd, setStart] = useState("09:30");
  const [eind_tijd, setEind] = useState("10:15");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/groups").then(r => setGroups(r.data || [])); }, []);
  const toggleWd = (n) => setWeekdays(w => w.includes(n) ? w.filter(x => x !== n) : [...w, n]);
  const submit = async () => {
    if (!group_id) return toast.error("Kies een groep");
    if (weekdays.length === 0) return toast.error("Kies minimaal één weekdag");
    setBusy(true);
    try {
      const r = await api.post(`/themes/${theme.id}/spread`, { group_id, weekdays, start_tijd, eind_tijd, skip_existing: true });
      toast.success(`${r.data.count} activiteiten ingepland` + (r.data.skipped?.length ? ` (${r.data.skipped.length} bestaande overgeslagen)` : ""));
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white" data-testid="spread-dialog">
        <DialogHeader>
          <DialogTitle>Spreid '{theme.titel}' over de planner</DialogTitle>
          <DialogDescription>{(theme.activity_ids||[]).length} activiteiten verdelen over {theme.start_date} → {theme.end_date}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <div><Label>Groep</Label>
            <Select value={group_id} onValueChange={setGroupId}>
              <SelectTrigger data-testid="spread-group"><SelectValue placeholder="Kies groep" /></SelectTrigger>
              <SelectContent className="bg-white">{groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Weekdagen</Label>
            <div className="flex flex-wrap gap-1 mt-1" data-testid="spread-weekdays">
              {["Ma","Di","Wo","Do","Vr","Za","Zo"].map((n,i) => (
                <button key={i} type="button" onClick={() => toggleWd(i)} data-testid={`spread-wd-${i}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium border ${weekdays.includes(i) ? "bg-[color:var(--sage)] text-white border-[color:var(--sage)]" : "bg-white text-neutral-700 border-neutral-300"}`}
                >{n}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Start</Label><Input type="time" value={start_tijd} onChange={e=>setStart(e.target.value)} data-testid="spread-start" /></div>
            <div><Label>Eind</Label><Input type="time" value={eind_tijd} onChange={e=>setEind(e.target.value)} data-testid="spread-eind" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuleren</Button>
          <Button onClick={submit} disabled={busy} style={{background:"var(--sage)"}} className="text-white" data-testid="spread-submit">
            {busy ? "Spreiden…" : "Spreid over planner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ThemaForm({ existing, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState(existing || {
    titel: "", omschrijving: "", start_date: today, end_date: today,
    activity_ids: [], target_group_ids: [], ouder_brief: "",
  });
  const [acts, setActs] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/activities", { params: { limit: 200 } }).then(r => setActs(r.data.items || []));
  }, []);
  const submit = async () => {
    if (!form.titel.trim()) return toast.error("Titel is verplicht");
    setBusy(true);
    try {
      const url = existing ? `/themes/${existing.id}` : "/themes";
      const method = existing ? "put" : "post";
      await api[method](url, form);
      toast.success(existing ? "Bijgewerkt" : "Thema aangemaakt");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggleAct = (aid) => {
    setForm(f => ({
      ...f,
      activity_ids: f.activity_ids.includes(aid)
        ? f.activity_ids.filter(x => x !== aid)
        : [...f.activity_ids, aid],
    }));
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto" data-testid="thema-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? "Thema bewerken" : "Nieuw thema / leerplan"}</DialogTitle>
          <DialogDescription>Koppel meerdere activiteiten aan een week, maand of vakantie.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <div><Label>Titel</Label><Input value={form.titel} onChange={e => setForm({...form, titel: e.target.value})} placeholder="Bijv. Herfstweek — Blaadjes en kleuren" data-testid="thema-titel" /></div>
          <div><Label>Omschrijving</Label><Textarea rows={2} value={form.omschrijving || ""} onChange={e => setForm({...form, omschrijving: e.target.value})} data-testid="thema-omschr" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Start</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} data-testid="thema-start" /></div>
            <div><Label>Eind</Label><Input type="date" value={form.end_date} min={form.start_date} onChange={e => setForm({...form, end_date: e.target.value})} data-testid="thema-end" /></div>
          </div>
          <div>
            <Label>Ouderbrief (optioneel)</Label>
            <Textarea rows={2} value={form.ouder_brief || ""} onChange={e => setForm({...form, ouder_brief: e.target.value})} placeholder="Tekst voor ouders over dit thema" />
          </div>
          <div>
            <Label>Activiteiten in dit thema ({form.activity_ids.length})</Label>
            <div className="max-h-56 overflow-y-auto border border-neutral-200 rounded p-2 space-y-1" data-testid="thema-activity-picker">
              {acts.map(a => (
                <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-neutral-50 p-1 rounded">
                  <input type="checkbox" checked={form.activity_ids.includes(a.id)} onChange={() => toggleAct(a.id)} data-testid={`thema-act-${a.id}`} />
                  <span className="flex-1">{a.titel}</span>
                  <span className="text-[10px] text-neutral-400">{a.duur_min}min · {(a.opvangsoort || []).join("/")}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuleren</Button>
          <Button onClick={submit} disabled={busy} style={{background:"var(--sage)"}} className="text-white" data-testid="thema-save">
            {busy ? "Opslaan…" : "Opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Kpi({label, v, testid}) {
  return (
    <Card className="p-3 bg-white shadow-none" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-2xl font-semibold text-neutral-800">{v}</div>
    </Card>
  );
}


function ReviewTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [bulkAi, setBulkAi] = useState({ running: false, processed: 0, remaining: null, total_before: null });

  const load = async () => {
    try {
      const r = await api.get("/activities/review/pending", { params: { limit: 500 } });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(a => a.id)));
  };

  const approve = async () => {
    if (selected.size === 0) return toast.info("Selecteer eerst activiteiten");
    setBusy(true);
    try {
      const r = await api.post("/activities/bulk-review", { ids: [...selected], action: "approve" });
      toast.success(`${r.data.updated} activiteit(en) goedgekeurd`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) return toast.error("Reden verplicht");
    setBusy(true);
    try {
      const r = await api.post("/activities/bulk-review", { ids: [...selected], action: "reject", reason: rejectReason.trim() });
      toast.success(`${r.data.updated} activiteit(en) afgewezen (gearchiveerd)`);
      setRejectDialog(false);
      setRejectReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const runBulkAI = async () => {
    setBulkAi({ running: true, processed: 0, remaining: null, total_before: null });
    let totalProcessed = 0;
    let firstTotal = null;
    while (true) {
      try {
        const r = await api.post("/activities/bulk-ai-materialen?limit=10");
        totalProcessed += r.data.processed || 0;
        if (firstTotal === null) firstTotal = r.data.total_before;
        setBulkAi({ running: true, processed: totalProcessed, remaining: r.data.remaining, total_before: firstTotal });
        if (!r.data.remaining || r.data.remaining === 0 || (r.data.processed === 0 && r.data.failed >= 0)) {
          if (r.data.remaining === 0) {
            toast.success(`Klaar! ${totalProcessed} activiteit(en) verrijkt met AI-materialenlijst.`);
          } else if (r.data.processed === 0) {
            toast.info(`Gestopt — ${r.data.remaining} activiteit(en) konden niet worden verrijkt.`);
          }
          break;
        }
      } catch (e) {
        toast.error(formatApiError(e));
        break;
      }
    }
    setBulkAi(prev => ({ ...prev, running: false }));
  };

  return (
    <div data-testid="review-tab">
      {/* Bulk AI-materialen (superadmin one-click) */}
      <Card className="p-4 mb-4 bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-200 shadow-none" data-testid="bulk-ai-mats-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <div className="font-semibold text-neutral-800">AI-materialenlijsten in bulk</div>
              <div className="text-xs text-neutral-600 mt-0.5">
                Verrijk alle activiteiten die nog geen materialenlijst hebben met één klik.
                {bulkAi.running && bulkAi.total_before !== null && (
                  <span className="ml-1 font-medium text-violet-700">
                    {bulkAi.processed}/{bulkAi.total_before} verwerkt · nog {bulkAi.remaining ?? "…"} te gaan
                  </span>
                )}
              </div>
            </div>
          </div>
          <Button
            onClick={runBulkAI}
            disabled={bulkAi.running}
            className="text-white bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600"
            data-testid="bulk-ai-mats-btn"
          >
            <Sparkles className="h-4 w-4 mr-1" />
            {bulkAi.running ? `Bezig… (${bulkAi.processed})` : "Vul alle ontbrekende lijsten"}
          </Button>
        </div>
      </Card>

      <Card className="p-4 mb-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 shadow-none">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-semibold text-neutral-800">Pedagogische review</div>
            <div className="text-xs text-neutral-600 mt-1">
              Democontent moet door een pedagoog worden nagekeken. Selecteer activiteiten en keur ze in bulk goed of wijs ze af.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" data-testid="review-pending-count">{total} openstaand</Badge>
            <Button size="sm" variant="outline" onClick={toggleAll} data-testid="review-select-all">
              {selected.size === items.length && items.length > 0 ? "Deselecteer alles" : "Selecteer alles"}
            </Button>
            <Button size="sm" onClick={approve} disabled={busy || selected.size === 0}
              style={{background:"var(--sage)"}} className="text-white" data-testid="review-approve-btn">
              Goedkeuren ({selected.size})
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRejectDialog(true)} disabled={busy || selected.size === 0}
              className="border-red-300 text-red-700 hover:bg-red-50" data-testid="review-reject-btn">
              Afwijzen ({selected.size})
            </Button>
          </div>
        </div>
      </Card>

      {items.length === 0 && (
        <Card className="p-8 bg-white shadow-none text-center" data-testid="review-empty">
          <ShieldCheck className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
          <div className="font-medium text-neutral-800">Alle activiteiten zijn gereviewd</div>
          <div className="text-xs text-neutral-500 mt-1">Er staat niets meer op de wachtlijst.</div>
        </Card>
      )}

      <div className="space-y-2">
        {items.map(a => (
          <Card key={a.id} className="p-3 bg-white shadow-none" data-testid={`review-row-${a.id}`}>
            <div className="flex items-start gap-3">
              <Checkbox
                checked={selected.has(a.id)}
                onCheckedChange={() => toggle(a.id)}
                data-testid={`review-check-${a.id}`}
                className="mt-1"
              />
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(expanded === a.id ? null : a.id)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-neutral-800 truncate">{a.titel}</div>
                    <div className="text-[11px] text-neutral-500">
                      {a.duur_min}min · {(a.opvangsoort||[]).join("/")} · {a.binnen_buiten}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {(a.ontwikkelingsgebieden||[]).slice(0,3).map(g =>
                      <Badge key={g} className="bg-[color:var(--sage-100)] text-[color:var(--sage)] text-[10px]">{g}</Badge>
                    )}
                  </div>
                </div>
                <div className="text-xs text-neutral-600 mt-1 line-clamp-2">{a.korte_omschrijving}</div>
                {expanded === a.id && (
                  <div className="mt-3 space-y-2 text-xs">
                    <div><strong>Voor ouders:</strong> {a.ouder_tekst || "—"}</div>
                    <div><strong>Intern:</strong> {a.interne_omschrijving || a.korte_omschrijving}</div>
                    {a.stappen?.length > 0 && (
                      <div>
                        <strong>Stappen:</strong>
                        <ol className="list-decimal ml-5 mt-1 space-y-0.5">
                          {a.stappen.map((s, i) => <li key={i}>{s}</li>)}
                        </ol>
                      </div>
                    )}
                    {a.veiligheid && <div className="text-red-700"><strong>Veiligheid:</strong> {a.veiligheid}</div>}
                    {a.materialen?.length > 0 && (
                      <div><strong>Materialen:</strong> {a.materialen.map(m => `${m.naam}${m.hoeveelheid ? ` (${m.hoeveelheid})` : ""}`).join(", ")}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent className="bg-white" data-testid="review-reject-dialog">
          <DialogHeader>
            <DialogTitle>Activiteiten afwijzen</DialogTitle>
            <DialogDescription>
              {selected.size} activiteit(en) worden gearchiveerd en niet meer getoond in de bibliotheek.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reden (verplicht)</Label>
            <Textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Waarom worden deze activiteiten afgewezen?"
              rows={3}
              data-testid="review-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectDialog(false)}>Annuleren</Button>
            <Button onClick={reject} disabled={busy || !rejectReason.trim()} className="bg-red-600 hover:bg-red-700 text-white" data-testid="review-reject-confirm">
              {busy ? "Bezig…" : "Afwijzen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SuggestiesTab() {
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [weather, setWeather] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [scheduling, setScheduling] = useState(null);
  const [liveWeather, setLiveWeather] = useState(null);
  const [wxLoading, setWxLoading] = useState(false);

  useEffect(() => {
    api.get("/groups").then(r => {
      setGroups(r.data || []);
      if (r.data?.length && !groupId) setGroupId(r.data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fetch weer zodra een groep + datum bekend zijn
  useEffect(() => {
    if (!groupId) return;
    setWxLoading(true);
    setLiveWeather(null);
    api.get(`/weather?group_id=${groupId}&datum=${datum}`)
      .then(r => setLiveWeather(r.data))
      .catch(() => setLiveWeather(null))
      .finally(() => setWxLoading(false));
  }, [groupId, datum]);

  const load = async () => {
    if (!groupId) { toast.error("Kies eerst een groep"); return; }
    setLoading(true);
    try {
      const q = `group_id=${groupId}&datum=${datum}${weather ? `&weather=${weather}` : ""}`;
      const { data } = await api.get(`/kdv-suggesties?${q}`);
      setData(data);
      if (!data.suggesties?.length) toast.info(data.reden || "Geen suggesties gevonden");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  };

  const inplannen = async (act) => {
    setScheduling(act.id);
    try {
      await api.post("/scheduled-activities", {
        activity_id: act.id,
        group_id: groupId,
        location_id: groups.find(g => g.id === groupId)?.location_id,
        datum,
        tijdblok: "ochtend",
        in_groepsverband: true,
      });
      toast.success(`'${act.titel}' ingepland voor ${datum}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Inplannen mislukt");
    }
    setScheduling(null);
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Groep</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger data-testid="suggest-group"><SelectValue placeholder="Kies groep" /></SelectTrigger>
              <SelectContent className="bg-white">
                {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Datum</Label>
            <div className="flex gap-1">
              <Input type="date" value={datum} onChange={e => setDatum(e.target.value)} data-testid="suggest-datum" className="flex-1" />
              <button type="button" onClick={() => {
                const t = new Date(); t.setDate(t.getDate() + 1);
                setDatum(t.toISOString().slice(0, 10));
              }} className="px-2 py-1 text-[10px] rounded border border-neutral-200 hover:bg-neutral-50 whitespace-nowrap"
                 data-testid="suggest-tomorrow-btn" title="Voorspelling voor morgen">
                → morgen
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Weer</Label>
            <Select value={weather} onValueChange={setWeather}>
              <SelectTrigger data-testid="suggest-weather"><SelectValue placeholder="Willekeurig" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value=" ">Willekeurig</SelectItem>
                <SelectItem value="zon">☀️ Zonnig (buiten)</SelectItem>
                <SelectItem value="regen">🌧️ Regen (binnen)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={load} disabled={loading} style={{ background: "var(--sage)" }} className="text-white w-full" data-testid="suggest-load-btn">
              <Sparkles className="h-4 w-4 mr-1" /> {loading ? "AI denkt na…" : "Suggesties voor vandaag"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Live weer-badge: direct zichtbaar zodra groep+datum bekend zijn */}
      {(wxLoading || liveWeather) && (() => {
        const w = liveWeather;
        const cat = w?.category || "zon";
        const Icon = cat === "regen" ? CloudRain : cat === "binnen" ? Cloud : Sun;
        const bg = cat === "regen"
          ? "from-blue-50 to-slate-50 border-blue-200"
          : cat === "binnen"
          ? "from-neutral-50 to-neutral-100 border-neutral-300"
          : "from-amber-50 to-orange-50 border-amber-200";
        const iconColor = cat === "regen" ? "text-blue-600" : cat === "binnen" ? "text-neutral-500" : "text-amber-500";
        const tomorrowISO = (() => { const t = new Date(); t.setDate(t.getDate() + 1); return t.toISOString().slice(0, 10); })();
        const label = w?.is_forecast
          ? (w.datum === tomorrowISO ? "Morgen" : `Voorspelling ${w.datum}`)
          : "Nu";
        const advies = cat === "regen"
          ? "Regen — kies binnen-activiteiten"
          : cat === "binnen"
          ? "Onstuimig — blijf binnen"
          : (w?.temperature != null && w.temperature >= 18)
            ? "Fijn weer — buiten spelen aanbevolen"
            : "Droog — buiten kan, kleed warm aan";
        return (
          <div className={`rounded-xl border bg-gradient-to-br ${bg} p-4 flex items-center gap-4`} data-testid="live-weather-badge">
            <div className={`rounded-full bg-white/70 p-3 ${iconColor}`}>
              {wxLoading ? <Cloud className="h-8 w-8 animate-pulse" /> : <Icon className="h-8 w-8" />}
            </div>
            <div className="flex-1 min-w-0">
              {wxLoading && (
                <div className="text-sm text-neutral-500">Weer laden…</div>
              )}
              {!wxLoading && w && (
                <>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-2xl font-semibold tabular-nums" data-testid="weather-temp">
                      {w.temperature != null ? `${w.temperature}°C` : "—"}
                    </span>
                    <span className="text-sm text-neutral-700" data-testid="weather-desc">{w.description}</span>
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500 px-2 py-0.5 rounded-full bg-white/60 border border-white">
                      {label}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-600 mt-0.5">
                    {w.location_name}
                    {w.precipitation > 0 && ` · ${w.precipitation} mm neerslag`}
                  </div>
                  <div className="text-xs text-neutral-700 mt-1 italic">{advies}</div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {data && data.suggesties?.length > 0 && (
        <>
          {data.weather && (
            <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-neutral-50 border border-neutral-200" data-testid="weather-badge">
              <span className="text-lg">{data.weather === "zon" ? "☀️" : data.weather === "regen" ? "🌧️" : "⛅"}</span>
              <span>
                {data.weather_auto_detected ? "Auto-gedetecteerd" : "Handmatig gekozen"}: <strong>{data.weather === "zon" ? "zonnig" : data.weather === "regen" ? "regen" : data.weather}</strong>
                {" — activiteiten aangepast op het weer"}
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="suggesties-list">
          {data.suggesties.map(s => (
            <Card key={s.id} className="p-4 bg-white flex flex-col" data-testid={`suggestie-${s.id}`}>
              <div className="text-sm font-medium mb-1">{s.titel}</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                {s.duur_min} min · {s.binnen_buiten} · {(s.categorieen || []).join(", ")}
              </div>
              <div className="text-xs text-neutral-700 mb-3 flex-1 italic">"{s.reason}"</div>
              <div className="text-[10px] text-neutral-500 mb-2">
                Leeftijd: {s.min_leeftijd_mnd}-{s.max_leeftijd_mnd} mnd · groep: {data.age_range_months?.[0]}-{data.age_range_months?.[1]} mnd
              </div>
              <Button size="sm" onClick={() => inplannen(s)} disabled={scheduling === s.id}
                style={{ background: "var(--sage)" }} className="text-white" data-testid={`inplan-${s.id}`}>
                {scheduling === s.id ? "Bezig…" : "1-klik inplannen"}
              </Button>
            </Card>
          ))}
          </div>
        </>
      )}
    </div>
  );
}
